/**
 * Selected Generated Assets
 *
 * Source of truth for the marketing asset winners that should be shown
 * on the public marketing surface.
 *
 * Primary responsibilities:
 * - Keep selected asset paths in one place
 * - Provide lightweight metadata for rendering
 *
 * Dependencies:
 * - none
 */

export type SelectedGeneratedAssetKey =
  | 'featuresHero'
  | 'spacesStack'
  | 'nexusHero'
  | 'nexusSignals'
  | 'aiReadinessReport'
  | 'aiReadinessAnalysis'
  | 'heroRibbon'
  | 'blogCollage'
  | 'supportStack'
  | 'canadaContour'
  | 'grainOverlay'
  | 'creativeStudio'
  | 'homepageNexusBridge'
  | 'footerSignalChat'
  | 'footerSignalSpaces'
  | 'footerSignalNexus'

interface SelectedGeneratedAsset {
  alt: string
  darkSrc?: string
  darkTone?: 'light' | 'dark'
  fit?: 'contain' | 'cover'
  lightSrc?: string
  lightTone?: 'light' | 'dark'
  mediaClassName?: string
  opaque?: boolean
  src: string
  tone: 'light' | 'dark'
}

export const selectedGeneratedAssets: Record<SelectedGeneratedAssetKey, SelectedGeneratedAsset> = {
  featuresHero: {
    alt: 'Abstract Losi feature system cluster with search, notes, chat, and creative tools',
    fit: 'cover',
    mediaClassName: 'scale-[1.08] sm:scale-[1.12]',
    src: '/marketing/generated/variants/features-hero-system-cluster-alpha--editorial-offset.webp',
    tone: 'light'
  },
  spacesStack: {
    alt: 'Layered Losi Spaces context stack with files, notes, voice snippets, and messages',
    mediaClassName: 'scale-[1.04] sm:scale-[1.08]',
    // diagram-precision is true cutout; editorial-offset bakes a dark stage plate
    src: '/marketing/generated/variants/spaces-context-stack-alpha--diagram-precision.webp',
    tone: 'light'
  },
  nexusHero: {
    alt: 'Dark Losi Nexus operations console with routed conversations and signals',
    fit: 'cover',
    lightSrc: '/marketing/generated/edits/nexus-hero-poster--light-refined.webp',
    lightTone: 'light',
    mediaClassName: 'scale-[1.02]',
    opaque: true,
    src: '/marketing/generated/variants/nexus-hero-poster--diagram-precision.webp',
    tone: 'dark'
  },
  nexusSignals: {
    alt: 'Connected signal flow for calls, chat, scheduling, and workspace routing',
    fit: 'cover',
    mediaClassName: 'scale-[1.04] sm:scale-[1.08]',
    src: '/marketing/generated/variants/nexus-signal-flow-alpha--editorial-offset.webp',
    tone: 'light'
  },
  aiReadinessReport: {
    alt: 'Layered AI readiness report with score rings, charts, and audit modules',
    mediaClassName: 'scale-[1.03] sm:scale-[1.06]',
    src: '/marketing/generated/variants/ai-readiness-report-layers-alpha--artifact-cleanup.webp',
    tone: 'light'
  },
  aiReadinessAnalysis: {
    alt: 'Light AI readiness analysis surface with evaluation panes and capability signals',
    darkSrc: '/marketing/generated/edits/ai-readiness-analysis--dark-clean.webp',
    darkTone: 'dark',
    fit: 'cover',
    mediaClassName: 'scale-[1.02]',
    opaque: true,
    src: '/marketing/generated/edits/ai-readiness-analysis--light-clean.webp',
    tone: 'light'
  },
  heroRibbon: {
    alt: 'Abstract atmospheric ribbon used as a Losi homepage accent',
    darkSrc: '/marketing/generated/edits/hero-atmosphere-ribbon-alpha--dark-clean.webp',
    darkTone: 'dark',
    src: '/marketing/generated/variants/hero-atmosphere-ribbon-alpha--editorial-offset.webp',
    tone: 'light'
  },
  blogCollage: {
    alt: 'Editorial collage of notes, charts, and research fragments',
    fit: 'cover',
    mediaClassName: 'scale-[1.04] sm:scale-[1.08]',
    src: '/marketing/generated/variants/blog-notes-collage-alpha--artifact-cleanup.webp',
    tone: 'light'
  },
  supportStack: {
    alt: 'Stacked support conversation cards with issue context and resolved state',
    fit: 'cover',
    mediaClassName: 'scale-[1.06] sm:scale-[1.1]',
    src: '/marketing/generated/variants/support-correspondence-stack-alpha--artifact-cleanup.webp',
    tone: 'light'
  },
  canadaContour: {
    alt: 'Abstract Canadian contour landscape with mist and restrained red accent',
    src: '/marketing/generated/variants/canada-contour-mist-alpha--editorial-offset.webp',
    tone: 'light'
  },
  grainOverlay: {
    alt: 'Subtle transparent grain and dust overlay',
    src: '/marketing/generated/soft-grain-dust-overlay.png',
    tone: 'light'
  },
  creativeStudio: {
    alt: 'Creative studio fragment composition with camera, waveform, and 3D forms',
    fit: 'cover',
    mediaClassName: 'scale-[1.05] sm:scale-[1.08]',
    src: '/marketing/generated/variants/features-creative-studio-fragments-alpha--editorial-offset.webp',
    tone: 'light'
  },
  homepageNexusBridge: {
    alt: 'Bridge from Losi work to Nexus operations shown through connected product fragments',
    fit: 'cover',
    mediaClassName: 'scale-[1.03] sm:scale-[1.06]',
    // diagram-precision is true cutout; editorial-offset bakes a dark stage plate
    src: '/marketing/generated/variants/homepage-nexus-bridge-alpha--diagram-precision.webp',
    tone: 'light'
  },
  footerSignalChat: {
    alt: 'Transparent chat orbit fragment used as a footer card accent',
    mediaClassName: 'scale-[1.02]',
    src: '/marketing/generated/edits/footer-signal-chat--corner-clean.webp',
    tone: 'light'
  },
  footerSignalSpaces: {
    alt: 'Transparent layered workspace fragment used as a footer card accent',
    mediaClassName: 'scale-[1.02]',
    src: '/marketing/generated/edits/footer-signal-spaces--corner-clean.webp',
    tone: 'light'
  },
  footerSignalNexus: {
    alt: 'Transparent routed operations fragment used as a footer card accent',
    mediaClassName: 'scale-[1.02]',
    src: '/marketing/generated/edits/footer-signal-nexus--corner-clean.webp',
    tone: 'light'
  }
}
